import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { AgentPermissionsProvider } from "./context/AgentPermissionsContext";
import { NotificationProvider } from "./context/NotificationContext";
import { NotificationCenterProvider } from "./context/NotificationCenterContext";
import { FilterMineProvider } from "./context/FilterMineContext";
import { GuidedTipsProvider } from "./context/GuidedTipsContext";
import GuidedTip from "./components/Help/GuidedTip";
import { PilotTrainingProvider } from "./context/PilotTrainingContext";
import PilotTrainingLauncher from "./components/Help/PilotTrainingLauncher";
import PilotTrainingPanel from "./components/Help/PilotTrainingPanel";
import Layout from "./components/Layout/Layout";
import Dashboard from "./pages/Dashboard";
import TicketsPage from "./pages/TicketsPage";
import TicketDetail from "./pages/TicketDetail";
import ReportsPage from "./pages/ReportsPage";
import CompaniesPage from "./pages/CompaniesPage";
import CustomerInfoPage from "./pages/CustomerInfoPage";
import TechniciansPage from "./pages/TechniciansPage";
import SettingsPage from "./pages/SettingsPage";
import ProjectDetail from "./pages/ProjectDetail";
import MaintenanceDetail from "./pages/MaintenanceDetail";
import ProjectsPage from "./pages/ProjectsPage";
import MaintenancePage from "./pages/MaintenancePage";
import ActivityLogPage from "./pages/ActivityLogPage";
import ContactsPage from "./pages/ContactsPage";
import ManagedDomainsPage from "./pages/ManagedDomainsPage";
import HelpdeskHomePage from "./pages/HelpdeskHomePage";
import AgentsPage from "./pages/AgentsPage";
import AgentDetailPage from "./pages/AgentDetailPage";
import EmployeeProfilePage from "./pages/EmployeeProfilePage";
import EmployeeSkillsPage from "./pages/EmployeeSkillsPage";
import EmployeeOperationsPage from "./pages/EmployeeOperationsPage";
import EmployeeSkillsLearningPage from "./pages/EmployeeSkillsLearningPage";
import LearningCenterPage from "./pages/LearningCenterPage";
import AgentRunsDashboardPage from "./pages/AgentRunsDashboardPage";
import AgentEvaluationsPage from "./pages/AgentEvaluationsPage";
import WorkforceConsolePage from "./pages/WorkforceConsolePage";
import AgentRunDetailPage from "./pages/AgentRunDetailPage";
import ModelProviderTransparencyPage from "./pages/ModelProviderTransparencyPage";
import RecoveryCenterPage from "./pages/RecoveryCenterPage";
import AttentionInboxPage from "./pages/AttentionInboxPage";
import ManagerConsolePage from "./pages/ManagerConsolePage";
import ApprovalCenterPage from "./pages/ApprovalCenterPage";
import PilotAcceptancePage from "./pages/PilotAcceptancePage";
import PilotFeedbackPage from "./pages/PilotFeedbackPage";
import ConversationsPage from "./pages/ConversationsPage";
import ConversationDetailPage from "./pages/ConversationDetailPage";
import HumanHandoffPage from "./pages/HumanHandoffPage";
import HumanHandoffBriefingPage from "./pages/HumanHandoffBriefingPage";
import RoutingDeskPage from "./pages/RoutingDeskPage";
import CollaborationPage from "./pages/CollaborationPage";
import WorkEvidenceCenterPage from "./pages/WorkEvidenceCenterPage";
import AgentPermissionsPage from "./pages/AgentPermissionsPage";
import EsetSpecialistPage from "./pages/EsetSpecialistPage";
import M365SpecialistPage from "./pages/M365SpecialistPage";
import AcronisSpecialistPage from "./pages/AcronisSpecialistPage";
import SpecialistWorkspacePage from "./pages/SpecialistWorkspacePage";
import ConnectionsPage from "./pages/ConnectionsPage";
import AdministrationPage from "./pages/AdministrationPage";
import PlatformHealthPage from "./pages/PlatformHealthPage";
import EmployeeBuilderPage from "./pages/EmployeeBuilderPage";
import CustomerProfilePage from "./pages/CustomerProfilePage";
import ScheduledChecksPage from "./pages/ScheduledChecksPage";
import ScheduledOperationsCenterPage from "./pages/ScheduledOperationsCenterPage";
import BugHunterPage from "./pages/BugHunterPage";
import SkillsPage from "./pages/SkillsPage";
import FeedbackPage from "./pages/FeedbackPage";
import TechScoutPage from "./pages/TechScoutPage";
import InnovationReviewCenterPage from "./pages/InnovationReviewCenterPage";
import BackupGuardianPage from "./pages/BackupGuardianPage";
import GuardianCenterPage from "./pages/GuardianCenterPage";
import AlertsPage from "./pages/AlertsPage";
import KnowledgePage from "./pages/KnowledgePage";
import NotificationSettingsPage from "./pages/NotificationSettingsPage";
import CommandCenterPage from "./pages/CommandCenterPage";
import RemoteOperationsPage from "./pages/RemoteOperationsPage";
import SystemMapLivePage from "./pages/SystemMapLivePage";
import HelpCenterPage from "./pages/HelpCenterPage";
import HandoffCenterPage from "./pages/HandoffCenterPage";
import TeamViewerPilotPage from "./pages/TeamViewerPilotPage";
import LoginPage from "./pages/LoginPage";
import AuthCallback from "./pages/AuthCallback";

function AppRoutes() {
  const { user, loading } = useAuth();
  const { pathname } = useLocation();

  // OAuth callback must render before auth guard so it can process the code
  if (pathname === "/auth/callback") return <AuthCallback />;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600" />
      </div>
    );
  }

  if (!user) return <LoginPage />;

  return (
    <Layout>
      <Routes>
        <Route path="/"              element={<Navigate to="/dashboard" replace />} />
        <Route path="/dashboard"     element={<Dashboard />} />
        <Route path="/tickets"       element={<TicketsPage />} />
        <Route path="/tickets/:id"   element={<TicketDetail />} />
        <Route path="/reports"           element={<ReportsPage />} />
        <Route path="/companies"         element={<CompaniesPage />} />
        <Route path="/technicians"       element={<TechniciansPage />} />
        <Route path="/settings"          element={<SettingsPage />} />
        <Route path="/projects"          element={<ProjectsPage />} />
        <Route path="/projects/:id"      element={<ProjectDetail />} />
        <Route path="/maintenance"       element={<MaintenancePage />} />
        <Route path="/maintenance/:id"   element={<MaintenanceDetail />} />
        <Route path="/activity-log"      element={<ActivityLogPage />} />
        <Route path="/contacts"          element={<ContactsPage />} />
        <Route path="/managed-domains"   element={<ManagedDomainsPage />} />
        <Route path="/helpdesk"                 element={<HelpdeskHomePage />} />
        <Route path="/agents"                   element={<AgentsPage />} />
        <Route path="/agents/:id"               element={<AgentDetailPage />} />
        <Route path="/agents/:id/operations"    element={<EmployeeOperationsPage />} />
        <Route path="/agents/:id/cv"            element={<EmployeeProfilePage />} />
        <Route path="/workforce"                element={<WorkforceConsolePage />} />
        <Route path="/agents/:id/skills"        element={<EmployeeSkillsPage />} />
        <Route path="/agents/:id/skills-learning" element={<EmployeeSkillsLearningPage />} />
        <Route path="/agents/:id/learning-center" element={<LearningCenterPage />} />
        <Route path="/agent-runs"               element={<AgentRunsDashboardPage />} />
        <Route path="/agent-runs/:id"           element={<AgentRunDetailPage />} />
        <Route path="/agent-evaluations"        element={<AgentEvaluationsPage />} />
        <Route path="/model-transparency"       element={<ModelProviderTransparencyPage />} />
        <Route path="/recovery"                 element={<RecoveryCenterPage />} />
        <Route path="/attention-inbox"          element={<AttentionInboxPage />} />
        <Route path="/manager-console"          element={<ManagerConsolePage />} />
        <Route path="/approval-center"          element={<ApprovalCenterPage />} />
        <Route path="/pilot-acceptance"         element={<PilotAcceptancePage />} />
        <Route path="/pilot-feedback"           element={<PilotFeedbackPage />} />
        <Route path="/conversations"            element={<ConversationsPage />} />
        <Route path="/conversations/:id"        element={<ConversationDetailPage />} />
        <Route path="/handoffs"                 element={<HumanHandoffPage />} />
        <Route path="/handoffs/:runId"          element={<HumanHandoffBriefingPage />} />
        <Route path="/routing-desk"             element={<RoutingDeskPage />} />
        <Route path="/collaboration"            element={<CollaborationPage />} />
        <Route path="/collaboration/:id"        element={<CollaborationPage />} />
        <Route path="/work-evidence"            element={<WorkEvidenceCenterPage />} />
        <Route path="/agent-permissions"        element={<AgentPermissionsPage />} />
        <Route path="/specialists/eset"         element={<EsetSpecialistPage />} />
        <Route path="/specialists/m365"        element={<M365SpecialistPage />} />
        <Route path="/specialists/acronis"      element={<AcronisSpecialistPage />} />
        <Route path="/specialists"           element={<Navigate to="/specialists/eset" replace />} />
        <Route path="/specialists/:domain"   element={<SpecialistWorkspacePage />} />
        <Route path="/knowledge"                element={<KnowledgePage />} />
        <Route path="/connections"       element={<ConnectionsPage />} />
        <Route path="/administration"    element={<AdministrationPage />} />
        <Route path="/platform-health"               element={<PlatformHealthPage />} />
        <Route path="/employee-builder"              element={<EmployeeBuilderPage />} />
        <Route path="/companies/:companyId/profile"  element={<CustomerProfilePage />} />
        <Route path="/customer-info" element={<CustomerInfoPage />} />
        <Route path="/customer-info/:companyId" element={<CustomerInfoPage />} />
        <Route path="/scheduled-checks"              element={<ScheduledChecksPage />} />
        <Route path="/scheduled-operations"          element={<ScheduledOperationsCenterPage />} />
        <Route path="/bug-hunter"                   element={<BugHunterPage />} />
        <Route path="/backup-guardian"              element={<BackupGuardianPage />} />
        <Route path="/guardian"                     element={<GuardianCenterPage />} />
        <Route path="/alerts"                       element={<AlertsPage />} />
        <Route path="/skills"                       element={<SkillsPage />} />
        <Route path="/feedback"                     element={<FeedbackPage />} />
        <Route path="/tech-scout"                   element={<TechScoutPage />} />
        <Route path="/innovation-review"            element={<InnovationReviewCenterPage />} />
        <Route path="/notification-settings" element={<NotificationSettingsPage />} />
        <Route path="/command-center"        element={<CommandCenterPage />} />
        <Route path="/remote-operations"     element={<RemoteOperationsPage />} />
        <Route path="/system-map"            element={<SystemMapLivePage />} />
        <Route path="/handoff-center"        element={<HandoffCenterPage />} />
        <Route path="/teamviewer-pilot"      element={<TeamViewerPilotPage />} />
        <Route path="/help"                  element={<HelpCenterPage />} />
      </Routes>
      <GuidedTip />
      <PilotTrainingLauncher />
      <PilotTrainingPanel />
    </Layout>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <AgentPermissionsProvider>
        <NotificationProvider>
          <NotificationCenterProvider>
            <FilterMineProvider>
              <GuidedTipsProvider>
                <PilotTrainingProvider>
                  <AppRoutes />
                </PilotTrainingProvider>
              </GuidedTipsProvider>
            </FilterMineProvider>
          </NotificationCenterProvider>
        </NotificationProvider>
      </AgentPermissionsProvider>
    </AuthProvider>
  );
}

